<script setup lang="ts">
import { inject,Ref,ref, reactive, computed, onMounted } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { format } from 'date-fns'
import { sendGraphQLQuery } from '@/services/gql-util'

interface Media {
	title: string
}

interface User {
	email: string
}

interface Transfer {
	name: string
}

interface FileTransfer {
	mid: number
	status: string
	updated: string
	time: string
	queued_at: string
	publish_at: string
	uid: number
	media: Media
	user: User
	transfer: Transfer
}

interface TransferFilters {
	vhost: number
	status?: string
	type?: number
	start: number
	limit: number
}
const vhostId = inject<Ref<string>>('vhostId');
const loading = ref(false)
const error = ref<string | null>(null)
const transfers = ref<FileTransfer[]>([])
const totalCount = ref(0)
const providers = ref<string[]>([])

const statusOptions = ['queued', 'assigned', 'transferring', 'success', 'failure']

const filters = reactive<TransferFilters>({
	vhost: vhostId && vhostId.value ? parseInt(vhostId.value):0,
	status: undefined,
	type: undefined,
	start: 0,
	limit: 100
})

const currentPage = computed(() => Math.floor(filters.start / filters.limit) + 1)

const formatDate = (dateString: string) => {
	try {
		return format(new Date(dateString), 'PPpp')
	} catch {
		return dateString
	}
}

const statusVariant = (status: string) => {
	switch (status) {
		case 'success': return 'default'
		case 'failure': return 'destructive'
		case 'queued': return 'secondary'
		case 'assigned': return 'default'
		case 'transferring': return 'default'
		default: return 'outline'
	}
}

const fetchTransfers = async () => {
	loading.value = true
	error.value = null

	try {
		const query = `
      query GetFileTransfers(
	$vhost: Int!, 
	$status: String, 
	$type: Int, 
	$start: Int, 
	$limit: Int
      ) {
	Transfers {
	  filetransfers(
	    vhost: $vhost,
	    status: $status,
	    type: $type,
	    start: $start,
	    limit: $limit
	  ) {
	    mid
	    status
	    updated
	    time
	    queued_at
	    publish_at
	    uid
	    media {
	      title
	    }
	    user {
	      email
	    }
	    transfer {
	      name
	    }
	  }
	}
      }
    `

		const variables = {
			vhost: filters.vhost,
			status: filters.status,
			type: filters.type,
			start: filters.start,
			limit: filters.limit
		}

		const response = await sendGraphQLQuery<{ Transfers: { filetransfers: FileTransfer[] } }>(query, variables)
		transfers.value = response.Transfers.filetransfers
 const names = transfers.value.map(t => t.transfer?.name)
      .filter(Boolean) // Remove null/undefined

    providers.value = [...new Set(names)]
	} catch (err) {
		error.value = err instanceof Error ? err.message : 'Failed to fetch transfers'
		console.error('Error fetching transfers:', err)
	} finally {
		loading.value = false
	}
}

const nextPage = () => {
	filters.start += filters.limit
	fetchTransfers()
}

const prevPage = () => {
	filters.start = Math.max(0, filters.start - filters.limit)
	fetchTransfers()
}
const provFilter = ref('');

onMounted(() => {
	fetchTransfers()
})
</script>

<template>
	<div class="w-full bg-background ">
		<!-- Desktop view (horizontal tabs) -->
		<div class="hidden md:flex justify-between items-center border-b py-2 px-5 ">
			<div class="border-b-2 border-background hover:border-muted-foreground mx-2 px-1 text-center text-subtitle flex-shink-0" :class="{ 'border-primary text-primary': provFilter == '', [`basis-1/${providers.length+1}`]: providers.length> 0 }" >
				<button class="uppercase text-subtitle mx-auto py-1" @click.prevent="provFilter=''">
					All
				</button>
			</div>
			<div class="border-b-2 border-background hover:border-muted-foreground mx-2 px-1 text-center text-subtitle flex-shink-0"  v-for="(provider,index) in providers"   :key="index" :class="{ 'border-primary text-primary': provFilter === provider, [`basis-1/${providers.length+1}`]: providers.length> 0 }" >
				<button class="uppercase text-subtitle mx-auto py-1" @click.prevent="provFilter=provider">
					{{provider }}
				</button>
			</div>
		</div>

	</div>

	<Card class="w-full">
	<CardHeader>
	<CardTitle class="font-normal text-subtitle">File Transfers</CardTitle>
	</CardHeader>
	<CardContent>
	<div class="flex flex-col gap-6">
		<!-- Filters -->
		<div class="flex flex-col sm:flex-row gap-4 items-end">
			<div class="grid w-full sm:max-w-xs gap-1.5">
				<label class="text-sm font-medium leading-none">Status</label>
				<Select v-model="filters.status">
					<SelectTrigger>
					<SelectValue placeholder="All Statuses" />
					</SelectTrigger>
					<SelectContent>
					<SelectItem v-for="status in statusOptions" :key="status" :value="status">
					{{ status }}
					</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<div class="grid w-full sm:max-w-xs gap-1.5">
				<label class="text-sm font-medium leading-none">Type</label>
				<Input type="number" v-model.number="filters.type" placeholder="Filter by type" min="0" />
			</div>

			<Button @click="fetchTransfers" :disabled="loading">
				<span v-if="loading" class="flex items-center gap-2">
					<svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					Loading...
				</span>
				<span v-else>Apply Filters</span>
			</Button>
		</div>

		<!-- Loading State -->
		<div v-if="loading" class="space-y-4">
			<Skeleton class="h-24 w-full" />
			<Skeleton class="h-24 w-full" />
			<Skeleton class="h-24 w-full" />
		</div>

		<!-- Error State -->
		<div v-else-if="error" class="rounded-lg bg-destructive/15 p-4">
			<p class="text-sm text-destructive">{{ error }}</p>
		</div>

		<!-- Empty State -->
		<div v-else-if="transfers.length === 0" class="text-center py-8 text-muted-foreground">
			No transfers found
		</div>

		<!-- Transfer List -->
		<div v-else class="space-y-4">
			<div v-for="transfer in transfers" :key="transfer.mid"> 
			<Card v-if="provFilter=='' || provFilter==transfer.transfer.name" class="hover:shadow-md transition-shadow" >
			    <CardContent class="p-6">
			    <div class="flex flex-col gap-4">
				    <div class="flex justify-between items-start">
					    <div>
						    <h3 class="font-semibold text-lg">{{ transfer.transfer.name }}</h3>
						    <p class="text-sm text-muted-foreground">
						    Media: {{ transfer.media.title }}
						    </p>
					    </div>
					    <Badge :variant="statusVariant(transfer.status)">
					    {{ transfer.status }}
					    </Badge>
				    </div>

				    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
					    <div class="space-y-1">
						    <p class="text-sm font-medium">Details</p>
						    <div class="text-sm text-muted-foreground grid grid-cols-2 gap-2">
							    <div>
								    <span class="font-medium">MID:</span> {{ transfer.mid }}
							    </div>
							    <div>
								    <span class="font-medium">UID:</span> {{ transfer.uid }}
							    </div>
							    <div>
								    <span class="font-medium">User:</span> {{ transfer.user.email }}
							    </div>
						    </div>
					    </div>

					    <div class="space-y-1">
						    <p class="text-sm font-medium">Timestamps</p>
						    <div class="text-sm text-muted-foreground space-y-1">
							    <div>
								    <span class="font-medium">Created:</span> {{ formatDate(transfer.time) }}
							    </div>
							    <div>
								    <span class="font-medium">Queued:</span> {{ formatDate(transfer.queued_at) }}
							    </div>
							    <div>
								    <span class="font-medium">Updated:</span> {{ formatDate(transfer.updated) }}
							    </div>
							    <div v-if="transfer.publish_at">
								    <span class="font-medium">Publish:</span> {{ formatDate(transfer.publish_at) }}
							    </div>
						    </div>
					    </div>
				    </div>
			    </div>
			    </CardContent>
			</Card>
		</div>
		</div>

		<!-- Pagination -->
		<div v-if="transfers.length > 0" class="flex items-center justify-center gap-4">
			<Button variant="outline" @click="prevPage" :disabled="filters.start <= 0 || loading" >
						 Previous
			</Button>
				<span class="text-sm text-muted-foreground">Page {{ currentPage }}</span>
				<Button 
				      variant="outline" 
				      @click="nextPage" 
				      :disabled="transfers.length < filters.limit || loading"
				      >
				      Next
				</Button>
		</div>
	</div>
	</CardContent>
	</Card>
</template>
